import React from 'react';
import { Box, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { useResizablePanels } from '../../../../hooks/useResizablePanels';

type Direction = 'horizontal' | 'vertical';

interface ResizablePanelsProps {
    primary: React.ReactNode;
    secondary: React.ReactNode;
    direction?: Direction;
    localStorageKey: string;
}

const baseContainerStyles = {
    display: 'flex',
    height: '100%',
    width: '100%',
};

const dividerBaseStyles = {
    flexShrink: 0,
    flexGrow: 0,
    position: 'relative',
    transition: 'background-color 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const panelBaseStyles = {
    flexShrink: 0,
    flexGrow: 0,
};

export const ResizablePanels: React.FC<ResizablePanelsProps> = ({
    primary,
    secondary,
    direction = 'vertical',
    localStorageKey,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { containerRef, panelSize, isDragging, handleMouseDown } =  useResizablePanels({
            minSize: 25,
            direction,
            localStorageKey,
        });

    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{primary}</Box>
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{secondary}</Box>
            </Box>
        );
    }

    const isHorizontal = direction === 'horizontal';

    const containerSx = {
        ...baseContainerStyles,
        flexDirection: isHorizontal ? 'row' : 'column',
        userSelect: isDragging ? 'none' : 'auto',
    };

    const primaryPanelSx = {
        ...panelBaseStyles,
        flexBasis: `${panelSize}%`,
        minWidth: isHorizontal ? '25%' : 'auto',
        minHeight: !isHorizontal ? '25%' : 'auto',
    };

    const secondaryPanelSx = {
        flex: 1,
        overflow: 'hidden',
        minWidth: isHorizontal ? '25%' : 'auto',
        minHeight: !isHorizontal ? '25%' : 'auto',
    };

    const dividerSx = {
        ...dividerBaseStyles,
        cursor: isHorizontal ? 'col-resize' : 'row-resize',
        backgroundColor: isDragging ? 'primary.main' : 'transparent',
        width: isHorizontal ? '4px' : '100%',
        height: isHorizontal ? '100%' : '4px',
        marginX: '10px',
        '&:hover': {
            backgroundColor: 'primary.main',
        },
        '&:hover .drag-indicator': {
            color: 'common.white',
        },
    };

    const iconSx = {
        color: isDragging ? 'common.white' : 'text.secondary',
        transform: isHorizontal ? 'none' : 'rotate(90deg)',
        transition: 'color 0.2s ease-in-out',
        fontSize: '16px',
    };

    return (
        <Box boxSizing='border-box' ref={containerRef} sx={containerSx}>
            <Box height='calc(100% - 0rem)' sx={primaryPanelSx}>{primary}</Box>

            <Tooltip
                title="Drag to resize"
                placement={isHorizontal ? 'right' : 'bottom'}
                arrow
            >
                <Box onMouseDown={handleMouseDown} sx={dividerSx}>
                    <DragIndicatorIcon className="drag-indicator" sx={iconSx} />
                </Box>
            </Tooltip>

            <Box height='calc(100% - 0rem)' sx={secondaryPanelSx}>{secondary}</Box>
        </Box>
    );
};
